/// <amd-module name="Wit/Common/ColumnOptionsPanel/BacklogsColumnOptionsPanelContainer" />
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableArray, ObservableValue, ReadyableObservableArray } from "VSS/Core/Observable";
import { caseInsensitiveContains } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { IBacklogColumn } from "Wit/Common/BacklogContentService/BacklogContentDataProviderTypes";
import { HubType, IBacklogFieldResponse } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogsHubContentService } from "Wit/Common/BacklogContentService/IBacklogsHubContentService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { IHubContentService } from "Wit/Common/BacklogContentService/IHubContentService";
import {
    IRollupColumn,
    getRollupColumnReferenceName,
    isRollupEqual
} from "Wit/Common/BacklogContentService/RollupHelpers";
import { ColumnOptionsPanel, IColumnOptionTableItem } from "Wit/Common/ColumnOptionsPanel/ColumnOptionsPanel";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { FieldUsages } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";

const FieldsDataProvider = "ms.vss-work-web.fields-data-provider";

export interface IColumnOptionsPanelContainerProps {
    onDismiss: () => void;
    teamId: string | undefined;
    hubService: IHubContentService;
}

export class BacklogsColumnOptionsPanelContainer extends VssComponent<IColumnOptionsPanelContainerProps> {
    private allFields = new ReadyableObservableArray<IFieldEntry>([]);
    private commonBacklogContentService: ICommonBacklogContentService;
    private backlogContentService: IHubContentService;
    private columnTableItems = new ObservableArray<IColumnOptionTableItem>([]);
    private dropdownItems = new ObservableArray<IListBoxItem<IFieldEntry>>([]);
    private loading = new ObservableValue<boolean>(true);
    private availableRollupColumns = new ObservableArray<IRollupColumn>([]);
    private workItemDataManager: IWorkItemDataManager;
    private columnOptionsFilteringAndOptimizationEnabled: boolean = false;

    public constructor(props: IColumnOptionsPanelContainerProps, context: IVssComponentContext) {
        super(props, context);
        this.backlogContentService = props.hubService;
        this.commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.workItemDataManager = this.context.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        this.columnOptionsFilteringAndOptimizationEnabled = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlags.ColumnOptionsFilteringAndOptimization,
            false
        );
    }

    public componentDidMount() {
        this.initializeFields();
    }

    public render() {
        return (
            <Observer dropdownItems={this.dropdownItems} rollupColumns={this.availableRollupColumns}>
                {(props: { dropdownItems: IListBoxItem<IFieldEntry>[]; rollupColumns: IRollupColumn[] }) => (
                    <ColumnOptionsPanel
                        loading={this.loading}
                        onDismiss={this.props.onDismiss}
                        onSubmit={this.onSubmit}
                        columnTableItems={this.columnTableItems}
                        dropdownItems={props.dropdownItems}
                        availableRollupColumns={props.rollupColumns}
                        hubType={HubType.Backlogs}
                    />
                )}
            </Observer>
        );
    }

    private initializeFields = async (): Promise<void> => {
        this.loading.value = true;
        const projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";

        // Gets all possible column values for the project
        const fieldsResultResponse = await this.getFields(projectId);

        // if user changed columns but hasn't pulled tree from backend, have to get latest columns from settingsService.
        let columns: IBacklogColumn[] | undefined = await this.backlogContentService.settingsServiceGetColumns();

        if (!columns || (columns && columns.length === 0)) {
            // first time you hit page, no settingsService values saved, so must get columns from backlogTree.
            columns = await this.commonBacklogContentService.getBacklogTree().value?.rollupColumns;
        }

        if (fieldsResultResponse && columns) {
            // Exclude virtual (WEF_) fields and non queryable fields
            const fields = fieldsResultResponse.data?.filter(field => field.usages !== FieldUsages.WorkItemTypeExtension && field.isQueryable);

            if (fields) {
                this.allFields.value = fields.sort((f1, f2) => f1.name.localeCompare(f2.name, navigator.language));
                this.allFields.ready.value = true;

                //  Maps allFields into something the dropdown can use
                this.dropdownItems.value = this.getAllDropdownItemsValues(this.allFields.value);

                // Gets all current visibleColumns from backlog and puts them in the Column Options modal
                this.initializeColumnTableItems(columns);
            }
        }
        const backlogHubContentService = this.context.pageContext.getService<IBacklogsHubContentService>("IBacklogsHubContentService");
        this.availableRollupColumns.value = await this.commonBacklogContentService.getAvailableRollupColumns(
            backlogHubContentService.getBacklogLevel()
        );

        // worst case, if columns can't be loaded, load blank modal and user can still choose their own columns from scratch
        this.loading.value = false;
    };

    private onSubmit = async (): Promise<void> => {
        const columns = await this.backlogContentService.settingsServiceGetColumns();

        // Get all the columns the user chose from the modal
        const selectedColumns = this.columnTableItems.value.map(tableItem => {
            if (tableItem.selectedField) {
                return tableItem.selectedField.value!.referenceName;
            } else if (tableItem.isRollupColumn) {
                return tableItem;
            }
        });

        const result: IBacklogColumn[] = selectedColumns.map(column => {
            // First, try to match to existing column that is already on the backlogs page so we can retain the same width and other prop
            const col = column as IColumnOptionTableItem;
            if (col.isRollupColumn) {
                const existingColumn = columns.find(field => {
                    if (field.rollupCalculation && col.rollupCalculation) {
                        return isRollupEqual(col.rollupCalculation, field.rollupCalculation);
                    }
                });

                if (existingColumn) {
                    return existingColumn;
                }

                const defaultWidth = 100;

                return {
                    width: defaultWidth,
                    rollup: true,
                    rollupCalculation: col.rollupCalculation,
                    name: getRollupColumnReferenceName(col.rollupCalculation!, col.isCustomRollup),
                    text: this.commonBacklogContentService.getRollupColumnNameAlignedWithBacklog(col.rollupCalculation!, col.friendlyNames)
                } as IBacklogColumn;
            } else {
                const existingColumn = columns.find(field => field.name === column);

                if (existingColumn) {
                    return existingColumn;
                }

                // If it's a new field, get its properties and give it a default width
                const field = this.allFields.value.find(field => field.referenceName === column);
                const defaultWidth = 100;

                return {
                    id: field?.id,
                    name: field?.referenceName,
                    text: field?.name,
                    fieldType: field?.type,
                    width: defaultWidth
                } as IBacklogColumn;
            }
        });

        try {
            // Must wait for first call to complete before refreshing tree to prevent race condition
            await this.backlogContentService.settingsServiceSetColumns(result);
            this.backlogContentService.refreshTree();
            this.props.onDismiss();
        } catch (err) {}
    };

    private getFields = async (projectId?: string): Promise<IBacklogFieldResponse | undefined> => {
        if (this.columnOptionsFilteringAndOptimizationEnabled) {
            const fieldProjectDataPromise = this.workItemDataManager.beginGetFieldProjectData(projectId ?? "");
            const fieldEntriesPromise = this.workItemDataManager.beginGetFields();
            const fieldIdMap: { [id: number]: boolean } = {};

            const fieldProjectData = await fieldProjectDataPromise;
            fieldProjectData?.projects?.forEach(proj => {
                if (proj.fieldIds) {
                    for (const fieldId of proj.fieldIds) {
                        if (!(fieldId in fieldIdMap)) {
                            fieldIdMap[fieldId] = true;
                        }
                    }
                }
            });

            const fieldEntries = await fieldEntriesPromise;
            return { data: fieldEntries?.filter(field => fieldIdMap[field.id]) };
        } else {
            //TODO: on ff clean up - remove IBacklogFieldResponse from vss-work-web\common\Services\backlog-content-service\BacklogContentTreeTypes.ts
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            const requestParams = { projectId };
            return contributionService.getDataAsync<IBacklogFieldResponse>(FieldsDataProvider, requestParams);
        }
    };

    private getAllDropdownItemsValues = (fields: IFieldEntry[]): IListBoxItem<IFieldEntry>[] => {
        return fields.map(field => ({
            id: field.id.toString(),
            text: field.name,
            data: field
        }));
    };

    private initializeColumnTableItems = (visibleColumns: IBacklogColumn[]): void => {
        if (visibleColumns) {
            this.columnTableItems.value = visibleColumns.map(col => {
                if (col.rollup) {
                    return {
                        required: false,
                        isRollupColumn: true,
                        rollupCalculation: col.rollupCalculation,
                        isCustomRollup: caseInsensitiveContains(col.name, "custom")
                    };
                } else {
                    let column = col as IBacklogColumn;
                    return {
                        // Once old backlogs page is retired, can remove all checks for fieldId and only retain id checks
                        selectedField: new ObservableValue<IFieldEntry | undefined>(
                            this.allFields.value.find(field => field.id === column.id || field.id === column.fieldId)
                        ),
                        required: column.id == 1 || column.fieldId == 1
                    };
                }
            });
        }
    };
}

Components.add("backlogs-column-options-container", BacklogsColumnOptionsPanelContainer);
